import {
  SUPERSIM_UTM_MEDIUM,
  SUPERSIM_CAMPAIGN_SCORE,
  SUPERSIM_CAMPAIGN_DEBT,
  SUPERSIM_EXPERIENCE_SESSION_KEY,
  SUPERSIM_CAMPAIGN_CANCEL_ACCOUNT,
  SUPERSIM_UTM_SOURCE,
  SUPERSIM_UTM_PRODUCT,
} from './supersim.constants';
import { SupersimCampaignsType, SupersimExperienceObj, SupersimExperienceType } from './supersim.types';

const SUPERSIM_EXPERIENCE_NAME: SupersimExperienceType = 'supersim';

const readUtmParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utmMedium: urlParams.get('utm_medium'),
    utmSource: urlParams.get('utm_source'),
    utmProduct: urlParams.get('utm_product'),
    utmCampaign: urlParams.get('utm_campaign'),
  };
};

/**
 * Extracts Supersim experience type from URL parameters.
 * Checks for specific utm_medium, utm_campaign, utm_source and utm_product combinations that indicate
 * the user came from a Supersim campaign.
 *
 *
 * @returns {SupersimExperienceType | null} The experience type or null if not a valid Supersim experience
 *
 * @example
 * // For URL: https://example.com/?utm_source=supersim&utm_medium=supersim&utm_product=score&utm_campaign=score
 * const experience = getSupersimExperienceFromURL();
 * // Returns: "supersim"
 *
 */
const getSupersimExperienceFromURL = (): SupersimExperienceType | null => {
  const params = readUtmParams();

  const { utmMedium, utmSource, utmProduct, utmCampaign } = params;
  const userCameFromSupersim = utmMedium === SUPERSIM_UTM_MEDIUM && utmSource === SUPERSIM_UTM_SOURCE;
  if (!userCameFromSupersim) return null;

  const userCameFromScore = utmProduct === SUPERSIM_UTM_PRODUCT;
  if (!userCameFromScore) return null;

  const userCameFromCustomCampaigns = [SUPERSIM_CAMPAIGN_DEBT, SUPERSIM_CAMPAIGN_SCORE, SUPERSIM_CAMPAIGN_CANCEL_ACCOUNT].includes(
    utmCampaign as SupersimCampaignsType
  );

  if (!userCameFromCustomCampaigns) return null;

  return SUPERSIM_EXPERIENCE_NAME;
};

/**
 * Initializes and stores the Supersim experience in session storage.
 * Extracts the experience type from URL parameters and persists it for later use.

 * @example
 * // Call this function when the page loads to capture Supersim experience
 * startSupersimExperience();
 */
export const startSupersimExperience = () => {
  const supersimExperience = getSupersimExperienceFromURL();
  if (!supersimExperience) return;
  const supersimExpObj: SupersimExperienceObj = {
    experience: supersimExperience,
  };

  sessionStorage.setItem(SUPERSIM_EXPERIENCE_SESSION_KEY, JSON.stringify(supersimExpObj));
};

/**
 * Retrieves the current Supersim experience from URL or session storage.
 * First checks URL parameters, then falls back to session storage.
 *
 * @returns {SupersimExperienceObj | null} The Supersim experience object or null if not found
 *
 * @example
 * const experience = getSupersimExperience();
 * if (experience) {
 *   console.log('User experience:', experience.experience); // "supersim"
 * }
 *
 */
export const getSupersimExperience = (): SupersimExperienceObj | null => {
  const experienceFromUrl = getSupersimExperienceFromURL();
  if (experienceFromUrl) return { experience: experienceFromUrl };

  const sessionObj = sessionStorage.getItem(SUPERSIM_EXPERIENCE_SESSION_KEY);
  if (sessionObj) return JSON.parse(sessionObj);

  return null;
};

export * from './supersim.types';
export * from './supersim.constants';
